import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import Button from "../common/Button";

const InventoryTable = ({
  products,
  brands,
  loading,
  selectedProducts,
  onSelectProduct,
  onSelectAll,
  onStockUpdate,
  onDeleteProduct,
  onRefresh,
  isMainAdmin = false  // ADD THIS
}) => {
  const { vendorProfile } = useAuth();
  // Filter products - hide mainAdminOnly products from regular vendors
// Filter products based on user type
const filteredProducts = products.filter(product => {
  // Main admin sees ONLY mainAdminOnly products
  if (isMainAdmin) {
    return product.mainAdminOnly === true || product.createdByMainAdmin === true;
  }
  
  // Regular vendors see ONLY non-admin products
  return product.mainAdminOnly !== true;
});
  const [editingStock, setEditingStock] = useState({});
  const [updatingStock, setUpdatingStock] = useState({}); // New state for stock update loading
  const [expandedProducts, setExpandedProducts] = useState({});
  const [productPurchases, setProductPurchases] = useState({});
  const [productPurchaseCounts, setProductPurchaseCounts] = useState({});
  const [loadingPurchases, setLoadingPurchases] = useState({});
  const [showBarcode, setShowBarcode] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStockBadge = (stock, reorderLevel = 10) => {
    const stockNum = Number(stock) || 0;
    const reorderNum = Number(reorderLevel) || 10;

    if (stockNum <= 0) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Out of Stock</span>;
    } else if (stockNum <= reorderNum) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Low: {stockNum}</span>;
    }
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">{stockNum}</span>;
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      Active: 'bg-green-100 text-green-800',
      Obsolete: 'bg-gray-100 text-gray-800',
      Damaged: 'bg-red-100 text-red-800',
      Returned: 'bg-yellow-100 text-yellow-800',
      'Out of Stock': 'bg-red-100 text-red-800',
      Discontinued: 'bg-gray-100 text-gray-800',
    };
    const colorClass = statusColors[status] || 'bg-gray-100 text-gray-800';
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>{status || 'Active'}</span>;
  };

  // Standard Barcode Display Component
  const StandardBarcodeDisplay = ({ barcode, productName, size = 'md' }) => {
    if (!barcode || barcode.length !== 13) {
      return <span className="text-xs text-gray-400">Invalid barcode</span>;
    }

    const width = size === 'sm' ? 150 : 250;
    const height = size === 'sm' ? 40 : 80;
    const fontSize = size === 'sm' ? 8 : 12;

    // EAN-13 bar patterns (unchanged)
    const leftOddPattern = {
      '0': '0001101', '1': '0011001', '2': '0010011', '3': '0111101',
      '4': '0100011', '5': '0110001', '6': '0101111', '7': '0111011',
      '8': '0110111', '9': '0001011'
    };

    const leftEvenPattern = {
      '0': '0100111', '1': '0110011', '2': '0011011', '3': '0100001',
      '4': '0011101', '5': '0111001', '6': '0000101', '7': '0010001',
      '8': '0001001', '9': '0010111'
    };

    const rightPattern = {
      '0': '1110010', '1': '1100110', '2': '1101100', '3': '1000010',
      '4': '1011100', '5': '1001110', '6': '1010000', '7': '1000100',
      '8': '1001000', '9': '1110100'
    };

    const firstDigitPatterns = {
      '0': 'OOOOOO', '1': 'OOEOEE', '2': 'OOEEOE', '3': 'OOEEEO',
      '4': 'OEOOEE', '5': 'OEEOOE', '6': 'OEEEOO', '7': 'OEOEOE',
      '8': 'OEOEEO', '9': 'OEEOEO'
    };

    const firstDigit = barcode[0];
    const leftDigits = barcode.substring(1, 7);
    const rightDigits = barcode.substring(7, 13);
    const pattern = firstDigitPatterns[firstDigit];

    let binaryString = '101'; // Start pattern

    for (let i = 0; i < 6; i++) {
      const digit = leftDigits[i];
      if (pattern[i] === 'O') {
        binaryString += leftOddPattern[digit];
      } else {
        binaryString += leftEvenPattern[digit];
      }
    }

    binaryString += '01010'; // Center pattern

    for (let i = 0; i < 6; i++) {
      const digit = rightDigits[i];
      binaryString += rightPattern[digit];
    }

    binaryString += '101'; // End pattern

    const bars = [];
    let x = 5;
    const barWidth = (width - 10) / binaryString.length;

    for (let i = 0; i < binaryString.length; i++) {
      if (binaryString[i] === '1') {
        bars.push(
          <rect
            key={i}
            x={x}
            y={5}
            width={barWidth}
            height={height - 20}
            fill="#000"
          />
        );
      }
      x += barWidth;
    }

    return (
      <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          {bars}
          <text x="8" y={height - 5} fontSize={fontSize - 2} fill="#000" fontFamily="monospace">
            {firstDigit}
          </text>
          <text x={width * 0.3} y={height - 5} fontSize={fontSize - 2} fill="#000" fontFamily="monospace" textAnchor="middle">
            {leftDigits}
          </text>
          <text x={width * 0.7} y={height - 5} fontSize={fontSize - 2} fill="#000" fontFamily="monospace" textAnchor="middle">
            {rightDigits}
          </text>
        </svg>
        <div className="text-center mt-2">
          <p className="text-sm text-gray-600 font-mono">{barcode}</p>
          {productName && (
            <p className="text-sm text-gray-800 font-medium mt-1 truncate" title={productName}>
              {productName}
            </p>
          )}
        </div>
      </div>
    );
  };

  const getBrandLogoUrl = (productBrand) => {
    if (!productBrand) {
      return `data:image/svg+xml;base64,${btoa(`
        <svg width="80" height="48" xmlns="http://www.w3.org/2000/svg">
          <rect width="80" height="48" fill="#E5E7EB"/>
          <text x="40" y="28" text-anchor="middle" font-family="Arial" font-size="10" fill="#6B7280">No Brand</text>
        </svg>
      `)}`;
    }

    if (!brands || brands.length === 0) {
      const brandName = typeof productBrand === 'string' ? productBrand : 'Brand';
      return `data:image/svg+xml;base64,${btoa(`
        <svg width="80" height="48" xmlns="http://www.w3.org/2000/svg">
          <rect width="80" height="48" fill="#F3F4F6"/>
          <text x="40" y="28" text-anchor="middle" font-family="Arial" font-size="10" fill="#374151">${brandName}</text>
        </svg>
      `)}`;
    }

    let brandObj = null;

    if (typeof productBrand === 'string') {
      brandObj = brands.find(b => b.id === productBrand) ||
        brands.find(b => b.name && b.name.toLowerCase() === productBrand.toLowerCase());
    } else if (Array.isArray(productBrand) && productBrand.length > 0) {
      const firstBrand = productBrand[0];
      brandObj = brands.find(b => b.id === firstBrand || (b.name && b.name.toLowerCase() === firstBrand.toLowerCase()));
    } else if (productBrand && typeof productBrand === 'object') {
      brandObj = productBrand;
    }

    if (brandObj && brandObj.logo) {
      return brandObj.logo;
    }

    const brandName = brandObj?.name || (typeof productBrand === 'string' ? productBrand : 'Brand');
    return `data:image/svg+xml;base64,${btoa(`
      <svg width="80" height="48" xmlns="http://www.w3.org/2000/svg">
        <rect width="80" height="48" fill="#F9FAFB" stroke="#D1D5DB"/>
        <text x="40" y="28" text-anchor="middle" font-family="Arial" font-size="9" fill="#374151">${brandName}</text>
      </svg>
    `)}`;
  };

  const getBrandName = (productBrand) => {
    if (!productBrand) return 'N/A';

    if (!brands || brands.length === 0) {
      if (typeof productBrand === 'string') return productBrand;
      if (Array.isArray(productBrand) && productBrand.length > 0) return productBrand[0];
      if (productBrand && typeof productBrand === 'object') return productBrand.name || 'N/A';
      return 'N/A';
    }

    if (typeof productBrand === 'string') {
      const brandObj = brands.find(b => b.id === productBrand || (b.name && b.name.toLowerCase() === productBrand.toLowerCase()));
      return brandObj?.name || productBrand;
    } else if (Array.isArray(productBrand) && productBrand.length > 0) {
      const firstBrand = productBrand[0];
      const brandObj = brands.find(b => b.id === firstBrand || (b.name && b.name.toLowerCase() === firstBrand.toLowerCase()));
      return brandObj?.name || firstBrand;
    } else if (productBrand && typeof productBrand === 'object') {
      return productBrand.name || 'N/A';
    }

    return 'N/A';
  };

  useEffect(() => {
    const loadAllPurchaseCounts = async () => {
      if (!vendorProfile?.uid || filteredProducts.length === 0) return; 

      try {
        const purchaseQuery = query(
          collection(db, 'purchaseInvoices'),
          where('vendorId', '==', vendorProfile.uid)
        );

        const purchaseSnapshot = await getDocs(purchaseQuery);
        const counts = {};
        const purchaseData = {};

       filteredProducts.forEach(product => {
          counts[product.id] = 0;
          purchaseData[product.id] = [];
        });

        purchaseSnapshot.docs.forEach(doc => {
          const invoice = { id: doc.id, ...doc.data() };

          invoice.items?.forEach(item => {
            const matchingProduct = products.find(p =>
              p.id === item.productId ||
              (item.productName && p.name?.toLowerCase().includes(item.productName.toLowerCase()))
            );

            if (matchingProduct) {
              counts[matchingProduct.id] = (counts[matchingProduct.id] || 0) + 1;

              if (!purchaseData[matchingProduct.id]) {
                purchaseData[matchingProduct.id] = [];
              }

              purchaseData[matchingProduct.id].push({
                invoiceId: invoice.id,
                invoiceNumber: invoice.invoiceNumber,
                supplierName: invoice.supplierName,
                purchaseDate: invoice.purchaseDate,
                quantity: item.quantity,
                rate: item.rate,
                amount: item.amount,
                status: invoice.status,
                createdAt: invoice.createdAt
              });
            }
          });
        });

        Object.keys(purchaseData).forEach(productId => {
          purchaseData[productId].sort((a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate));
        });

        setProductPurchaseCounts(counts);
        setProductPurchases(purchaseData);
      } catch (error) {
        console.error('Error loading purchase counts:', error);
      }
    };

    loadAllPurchaseCounts();
 }, [vendorProfile?.uid, filteredProducts]);

  const updateStockFromPurchases = async (productId, quantity) => {
    try {
      const productRef = doc(db, 'products', productId);
      const currentProduct = products.find(p => p.id === productId);
      const newStock = (currentProduct?.stock || 0) + quantity;

      await updateDoc(productRef, {
        stock: newStock,
        updatedAt: new Date()
      });

      if (onRefresh) {
        onRefresh();
      }

      return newStock;
    } catch (error) {
      console.error('Error updating stock from purchase:', error);
      throw error;
    }
  };

  const loadDetailedPurchaseHistory = async (productId) => {
    if (loadingPurchases[productId] || productPurchases[productId]?.length > 0) return;

    setLoadingPurchases(prev => ({ ...prev, [productId]: true }));

    try {
      setLoadingPurchases(prev => ({ ...prev, [productId]: false }));
    } catch (error) {
      console.error('Error loading detailed purchase history:', error);
      setLoadingPurchases(prev => ({ ...prev, [productId]: false }));
    }
  };

  const toggleProductExpansion = (productId) => {
    setExpandedProducts(prev => {
      const isExpanded = !prev[productId];

      if (isExpanded) {
        loadDetailedPurchaseHistory(productId);
      }

      return { ...prev, [productId]: isExpanded };
    });
  };

  const handleStockEdit = (productId, currentStock) => {
    setEditingStock({ ...editingStock, [productId]: currentStock.toString() });
  };

  const handleStockChange = (productId, value) => {
    setEditingStock({ ...editingStock, [productId]: value });
  };

  // Enhanced stock save function with loading state
  const handleStockSave = async (productId) => {
    const newStock = parseInt(editingStock[productId], 10);
    if (isNaN(newStock) || newStock < 0) return;

    // Set loading state for this specific product
    setUpdatingStock(prev => ({ ...prev, [productId]: true }));

    try {
      await onStockUpdate(productId, newStock);
      setEditingStock(prev => ({ ...prev, [productId]: undefined }));

      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Error updating stock:', error);
      // You might want to show an error message here
    } finally {
      // Clear loading state
      setUpdatingStock(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handleStockCancel = (productId) => {
    setEditingStock({ ...editingStock, [productId]: undefined });
    setUpdatingStock(prev => ({ ...prev, [productId]: false })); // Clear loading state on cancel
  };

  const handleImageError = (e) => {
    e.target.style.display = 'none';
    e.target.nextElementSibling.style.display = 'flex';
  };

  const getDisplayImageUrl = (product) => {
    if (product.imageUrls && Array.isArray(product.imageUrls) && product.imageUrls.length > 0) {
      const validUrls = product.imageUrls.filter(url => url && typeof url === 'string');
      if (validUrls.length > 0) return validUrls[0];
    }
    return null;
  };

  const toggleBarcode = (productId) => {
    setShowBarcode(showBarcode === productId ? null : productId);
  };

  const allSelected = filteredProducts.length > 0 && selectedProducts.length === filteredProducts.length;  // Changed

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <input
                id="select-all"
                type="checkbox"
                className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                checked={allSelected}
                onChange={(e) => onSelectAll(e.target.checked)}
              />
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Info</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Part Number</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brand</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {loading && products.length === 0 ? (
            <tr>
              <td colSpan="9" className="px-6 py-4 text-center text-sm text-gray-500">
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                </div>
                <p className="mt-2">Loading products...</p>
              </td>
            </tr>
        ) : filteredProducts.length === 0 ? ( 
            <tr>
              <td colSpan="9" className="px-6 py-4 text-center text-sm text-gray-500">
                No products found.
                <Link to="/inventory/add" className="ml-1 text-orange-600 hover:text-orange-900">Add a product</Link>
              </td>
            </tr>
          ) : (
           filteredProducts.map((product) => (
              <React.Fragment key={product.id}>
                <tr className={`${selectedProducts.includes(product.id) ? 'bg-orange-50' : 'hover:bg-gray-50'}`}>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <input
                      id={`select-${product.id}`}
                      type="checkbox"
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      checked={selectedProducts.includes(product.id)}
                      onChange={(e) => onSelectProduct(product.id, e.target.checked)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Link to={`/inventory/view/${product.id}`} className="block hover:bg-gray-50 rounded-lg p-1 transition-colors">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-12 w-12 relative">
                          {getDisplayImageUrl(product) ? (
                            <>
                              <img
                                className="h-12 w-12 rounded object-cover"
                                src={getDisplayImageUrl(product)}
                                alt={product.name || 'Product image'}
                                onError={handleImageError}
                              />
                              <div className="h-12 w-12 rounded bg-gray-200 items-center justify-center text-gray-500 hidden absolute inset-0">
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                            </>
                          ) : (
                            <div className="h-12 w-12 rounded bg-gray-200 flex items-center justify-center text-gray-500">
                              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="ml-4 min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate hover:text-orange-600 transition-colors" title={product.name}>
                            {product.name || 'Unnamed Product'}
                          </div>
                          {product.description && (
                            <div className="text-sm text-gray-500 truncate max-w-48" title={product.description}>
                              {product.description.length > 10
                                ? `${product.description.substring(0, 10)}...`
                                : product.description
                              }
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono text-gray-900" title={product.partNumber}>{product.partNumber || 'N/A'}</div>
                    {product.sku && product.sku !== product.partNumber && (
                      <div className="text-xs text-gray-500 font-mono" title={`SKU: ${product.sku}`}>{product.sku}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {product.categories && product.categories.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {product.categories[0].name || product.categories[0]}
                        </span>
                        {product.categories.length > 1 && (
                          <span className="text-xs text-gray-400">+{product.categories.length - 1}</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {product.brand || product.brands ? (
                      (() => {
                        const brandData = product.brand || product.brands;
                        let brandsToShow = [];

                        if (typeof brandData === 'string') {
                          brandsToShow = [brandData];
                        } else if (Array.isArray(brandData)) {
                          brandsToShow = brandData;
                        } else if (brandData && typeof brandData === 'object') {
                          brandsToShow = [brandData];
                        }

                        return (
                          <div className="flex flex-col space-y-1">
                            {brandsToShow.slice(0, 2).map((brand, index) => {
                              const brandName = getBrandName(brand);
                              const brandLogo = getBrandLogoUrl(brand);

                              return (
                                <div key={index} className="flex items-center space-x-2">
                                  <div className="relative">
                                    <img
                                      className="h-6 w-12 object-contain rounded"
                                      src={brandLogo}
                                      alt={brandName}
                                      title={brandName}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextElementSibling.style.display = 'flex';
                                      }}
                                    />
                                    <div className="h-6 w-12 bg-gray-200 items-center justify-center text-gray-600 hidden absolute inset-0 flex rounded text-xs font-medium">
                                      {brandName.substring(0, 4)}
                                    </div>
                                  </div>
                                  <div className="text-xs text-gray-600 truncate max-w-16" title={brandName}>
                                    {brandName}
                                  </div>
                                </div>
                              );
                            })}
                            {brandsToShow.length > 2 && (
                              <div className="text-xs text-gray-400 text-center">
                                +{brandsToShow.length - 2} more
                              </div>
                            )}
                          </div>
                        );
                      })()
                    ) : (
                      <span className="text-sm text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingStock[product.id] !== undefined ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          value={editingStock[product.id]}
                          onChange={(e) => handleStockChange(product.id, e.target.value)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          disabled={updatingStock[product.id]}
                        />

                        {/* Save button with loading state */}
                        <button
                          onClick={() => handleStockSave(product.id)}
                          disabled={updatingStock[product.id]}
                          className="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={updatingStock[product.id] ? "Updating..." : "Save"}
                        >
                          {updatingStock[product.id] ? (
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>

                        {/* Cancel button */}
                        {/* Cancel button */}
                        <button
                          onClick={() => handleStockCancel(product.id)}
                          disabled={updatingStock[product.id]}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Cancel"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => !updatingStock[product.id] && handleStockEdit(product.id, product.stock)}
                        title={updatingStock[product.id] ? "Updating stock..." : "Click to edit stock"}
                        className={`cursor-pointer ${updatingStock[product.id] ? 'opacity-50' : ''}`}
                      >
                        <div className="flex flex-col">
                          <div className="flex items-center space-x-2">
                            {getStockBadge(product.stock, product.reorderLevel)}
                            {updatingStock[product.id] && (
                              <svg className="w-4 h-4 animate-spin text-orange-600" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            )}
                          </div>
                          {product.lastStockUpdate && (
                            <div className="text-xs text-gray-400 mt-1">
                              Last updated: {new Date(product.lastStockUpdate.timestamp?.toDate()).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{formatCurrency(product.price || product.sellingPrice)}</div>
                    {product.comparePrice && (
                      <div className="text-xs text-gray-500 line-through">{formatCurrency(product.comparePrice)}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(product.status)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex flex-col items-center space-y-2">
                      {/* Primary Action Buttons Row */}
                      <div className="flex items-center space-x-3">
                        <Link
                          to={`/inventory/view/${product.id}`}
                          className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 transition-all duration-200 touch-manipulation"
                          title="View details"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </Link>
                        <Link
                          to={`/inventory/edit/${product.id}`}
                          className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-orange-50 text-orange-600 hover:bg-orange-100 hover:text-orange-700 transition-all duration-200 touch-manipulation"
                          title="Edit product"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </Link>

                      </div>

                      {/* Secondary Action Buttons Row */}
                      <div className="flex items-center space-x-3">
                        {product.barcode && (
                          <button
                            onClick={() => toggleBarcode(product.id)}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-700 transition-all duration-200 touch-manipulation"
                            title="View barcode"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1zm12 0h2a1 1 0 001-1V6a1 1 0 00-1-1h-2a1 1 0 00-1 1v1a1 1 0 001 1zM5 20h2a1 1 0 001-1v-1a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1z" />
                            </svg>
                          </button>
                        )}

                        <button
                          onClick={() => onDeleteProduct(product.id)}
                          className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 transition-all duration-200 touch-manipulation"
                          title="Delete product"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>

                {expandedProducts[product.id] && (
                  <tr className="bg-gray-50">
                    <td colSpan="9" className="px-6 py-4">
                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-medium text-gray-900 flex items-center">
                            <svg className="h-4 w-4 mr-2 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                            Purchase History ({productPurchaseCounts[product.id]} invoices)
                          </h4>
                          <div className="flex items-center space-x-2">
                            <Link
                              to={`/billing?tab=create-purchase&productId=${product.id}&productName=${encodeURIComponent(product.name)}`}
                              className="text-xs text-orange-600 hover:text-orange-800 font-medium flex items-center"
                            >
                              <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              Add Purchase
                            </Link>
                          </div>
                        </div>

                        {productPurchases[product.id]?.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {productPurchases[product.id].map((purchase, index) => (
                                  <tr key={index} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 text-xs">
                                      <Link
                                        to={`/billing/purchase/${purchase.invoiceId}`}
                                        className="text-blue-600 hover:text-blue-800 font-medium"
                                      >
                                        {purchase.invoiceNumber}
                                      </Link>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-gray-900">{purchase.supplierName}</td>
                                    <td className="px-3 py-2 text-xs text-gray-900">{formatDate(purchase.purchaseDate)}</td>
                                    <td className="px-3 py-2 text-xs text-gray-900 text-right">{purchase.quantity}</td>
                                    <td className="px-3 py-2 text-xs text-gray-900 text-right">{formatCurrency(purchase.rate)}</td>
                                    <td className="px-3 py-2 text-xs text-gray-900 font-medium text-right">{formatCurrency(purchase.amount)}</td>
                                    <td className="px-3 py-2 text-xs text-center">
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${purchase.status === 'received' ? 'bg-green-100 text-green-800' :
                                        purchase.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                          'bg-gray-100 text-gray-800'
                                        }`}>
                                        {purchase.status || 'N/A'}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-center">
                                      <Link
                                        to={`/billing/purchase/${purchase.invoiceId}`}
                                        className="text-blue-600 hover:text-blue-800"
                                        title="View invoice"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                      </Link>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>

                            <div className="mt-4 bg-purple-50 rounded-lg p-3">
                              <div className="flex justify-between items-center text-sm">
                                <span className="text-purple-800 font-medium">Purchase Summary:</span>
                                <div className="text-purple-700">
                                  <span className="mr-4">Total Qty: {productPurchases[product.id].reduce((sum, p) => sum + (p.quantity || 0), 0)}</span>
                                  <span>Total Amount: {formatCurrency(productPurchases[product.id].reduce((sum, p) => sum + (p.amount || 0), 0))}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                            <p className="mt-2 text-sm text-gray-500">No purchase history found</p>
                            <p className="text-xs text-gray-400 mb-4">Create a purchase invoice to track supplier purchases</p>
                            <Link
                              to={`/billing?tab=create-purchase&productId=${product.id}&productName=${encodeURIComponent(product.name)}`}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                            >
                              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              Create First Purchase
                            </Link>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>

      {/* Barcode Modal */}
      {showBarcode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 relative">
            <button
              className="absolute top-2 right-2 text-gray-600 hover:text-gray-900"
              onClick={() => setShowBarcode(null)}
              title="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <StandardBarcodeDisplay
        barcode={filteredProducts.find(p => p.id === showBarcode)?.barcode}  // Changed
        productName={filteredProducts.find(p => p.id === showBarcode)?.name}  // Changed
        size="md"
      />
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryTable;